<div class="calendar-page">
  <div class="calendar-layout">
    <div class="calendar-sidebar">
      <!-- Déplacement de la recherche d'événements au-dessus du datepicker -->
      <div class="event-search-container mb-2">
        <form
          [formGroup]="eventsSearchForm"
          class="flex"
          (submit)="applySearchImmediate($event)"
        >
          <p-inputGroup styleClass="w-full">
            <input
              formControlName="searchEvents"
              type="text"
              pInputText
              placeholder="Rechercher parmi les événements"
              (keydown.enter)="applySearchImmediate($event)"
            />
            <p-inputgroup-addon
              *ngIf="eventsSearchForm.get('searchEvents')?.value"
            >
              <p-button
                icon="pi pi-times"
                severity="secondary"
                variant="text"
                (onClick)="resetEventSearch()"
                title="Effacer la recherche"
              ></p-button>
            </p-inputgroup-addon>
          </p-inputGroup>
        </form>

        <!-- Afficher le nombre d'événements trouvés -->
        <div
          *ngIf="
            (filteredEvents$ | async)?.length !== (allEvents$ | async)?.length
          "
          class="mt-1 text-xs"
        >
          {{ (filteredEvents$ | async)?.length }} résultat(s) sur
          {{ (allEvents$ | async)?.length }}
        </div>
      </div>

      <p-datepicker
        [firstDayOfWeek]="1"
        [inline]="true"
        [showTime]="false"
        (onSelect)="gotoDate($event)"
      />

      <form [formGroup]="filtersForm">
        <div
          class="flex gap-4 justify-center items-center border border-zinc-700 rounded-md bg-zinc-950 p-2 my-2 mx-auto w-fit"
        >
          @for (society of mlSocieties; track $index) {
            <app-custom-checkbox
              formControlName="societyKeys"
              [value]="society"
              [hoverColorClass]="'text-' + society"
            >
              <i checkbox-icon [class]="'tll tll-24 fa-logo-' + society"></i>
            </app-custom-checkbox>
          }
        </div>
        <p-iconField iconPosition="left">
          <p-inputIcon styleClass="pi pi-search" />
          <input
            formControlName="search"
            class="w-full"
            type="text"
            pInputText
            placeholder="Rechercher"
          />
        </p-iconField>
      </form>

      <div class="flex flex-wrap max-w-[348px] gap-1 my-1">
        @for (user of usersForm.value.users; track user.id) {
          <p-chip
            [style]="{ 'border-left': '4px solid ' + (user.color || '#ccc') }"
            styleClass="text-sm user-chip"
            label="{{ user.initials }}"
            [removable]="true"
            (onRemove)="removeUser(user)"
          ></p-chip>
        }
      </div>

      <form [formGroup]="usersForm" class="users-list">
        <div
          *ngIf="loadingUsers"
          class="absolute right-0 left-0 top-0 bottom-0 grid bg-neutral-950 bg-opacity-70"
        >
          <p-progressSpinner ariaLabel="loading" class="m-auto" />
        </div>

        <ng-container *ngIf="users$ | async as users">
          <div
            *ngFor="let user of users; trackBy: trackById"
            class="flex justify-between items-center border-b border-zinc-800 py-1"
          >
            <div class="flex gap-2 items-center">
              <p-checkbox
                [value]="user"
                formControlName="users"
                [id]="user.id"
              />
              <span
                class="color-indicator"
                [style.background-color]="user.color || '#ccc'"
                title="Couleur de l'utilisateur sur le calendrier"
              ></span>
              <label [for]="user.id">
                {{ user.firstname }} {{ user.lastname }}
              </label>
            </div>

            <!-- N'afficher le bouton d'itinéraire que pour les techniciens sélectionnés -->
            <button
              *ngIf="isUserSelected(user)"
              pButton
              icon="pi pi-map"
              class="p-button-rounded p-button-text p-button-sm route-button"
              title="Voir l'itinéraire journalier"
              (click)="openDateSelector($event, user)"
            ></button>
          </div>

          <p *ngIf="(users?.length || 0) === 0" class="m-auto">
            Aucun utilisateur
          </p>
        </ng-container>
      </form>
    </div>
    <div class="calendar-main">
      <!-- En-tête du calendrier avec itinéraires -->
      <div
        class="calendar-header bg-neutral-800 p-2 border-b border-neutral-700 flex items-center justify-between mb-2"
      >
        <div class="flex items-center">
          <h4 class="text-sm font-medium m-0 mr-4">
            <i class="pi pi-calendar mr-2"></i> Planning
          </h4>
          <!-- Si aucun technicien n'est sélectionné -->
          @if (!selectedTechnicians.length) {
            <div class="text-xs text-neutral-400">
              Sélectionnez un technicien pour voir les itinéraires
            </div>
          } @else if (selectedTechnicians.length === 1) {
            <!-- Si un seul technicien est sélectionné -->
            <div class="route-links flex items-center gap-1">
              <span class="text-xs text-neutral-400 mr-1">Itinéraires:</span>
              @for (day of getWeekDays(); track $index) {
                <button
                  type="button"
                  class="route-day-link text-xs px-2 py-1 rounded cursor-pointer flex flex-col items-center"
                  [ngClass]="{
                    'bg-blue-900 hover:bg-blue-800':
                      day.isToday && day.hasInterventions,
                    'bg-neutral-700 hover:bg-neutral-600':
                      !day.isToday && day.hasInterventions,
                    'bg-neutral-800 opacity-50 cursor-not-allowed':
                      !day.hasInterventions,
                  }"
                  [title]="
                    day.hasInterventions
                      ? 'Itinéraire du ' +
                        day.longFormat +
                        ' pour ' +
                        selectedTechnicians[0].firstname
                      : 'Aucune intervention programmée pour ' +
                        selectedTechnicians[0].firstname +
                        ' le ' +
                        day.longFormat
                  "
                  (click)="openDailyRouteUrl(selectedTechnicians[0], day.date)"
                >
                  <span>{{ day.shortName }}</span>
                  <span class="text-[10px] opacity-80">{{
                    day.date | date: 'dd'
                  }}</span>
                  <span
                    class="text-[8px] text-green-400 font-bold"
                    *ngIf="day.hasInterventions"
                  >
                    {{ day.interventionCount }}
                  </span>
                </button>
              }
            </div>
          } @else {
            <!-- Si plusieurs techniciens sont sélectionnés -->
            <div class="flex items-center gap-2">
              <div class="text-xs">Itinéraires pour</div>
              <div class="tech-selector flex gap-1">
                @for (tech of selectedTechnicians; track tech.id) {
                  <div
                    class="tech-avatar w-6 h-6 rounded-full flex items-center justify-center cursor-pointer border-2"
                    [style.background-color]="tech.color || '#666'"
                    [style.border-color]="
                      selectedTechForRoutes?.id === tech.id
                        ? '#fff'
                        : 'transparent'
                    "
                    [title]="tech.firstname + ' ' + tech.lastname"
                    (click)="selectTechnicianForRoutes(tech)"
                  >
                    <span class="text-white text-xs">{{
                      getTechnicianInitials(tech)
                    }}</span>
                  </div>
                }
              </div>

              <!-- Afficher les liens pour le technicien sélectionné -->
              @if (selectedTechForRoutes) {
                <div class="route-links flex items-center gap-1 ml-2">
                  <span class="text-xs text-neutral-400 mr-1"
                    >Itinéraires:</span
                  >
                  @for (day of getWeekDays(); track $index) {
                    <button
                      type="button"
                      class="route-day-link text-xs px-2 py-1 rounded cursor-pointer flex flex-col items-center"
                      [ngClass]="{
                        'bg-blue-900 hover:bg-blue-800':
                          day.isToday && day.hasInterventions,
                        'bg-neutral-700 hover:bg-neutral-600':
                          !day.isToday && day.hasInterventions,
                        'bg-neutral-800 opacity-50 cursor-not-allowed':
                          !day.hasInterventions,
                      }"
                      [title]="
                        day.hasInterventions
                          ? 'Itinéraire du ' +
                            day.longFormat +
                            ' pour ' +
                            selectedTechForRoutes.firstname
                          : 'Aucune intervention programmée pour ' +
                            selectedTechForRoutes.firstname +
                            ' le ' +
                            day.longFormat
                      "
                      (click)="
                        openDailyRouteUrl(selectedTechForRoutes, day.date)
                      "
                    >
                      <span>{{ day.shortName }}</span>
                      <span class="text-[10px] opacity-80">{{
                        day.date | date: 'dd'
                      }}</span>
                      <span
                        class="text-[8px] text-green-400 font-bold"
                        *ngIf="day.hasInterventions"
                      >
                        {{ day.interventionCount }}
                      </span>
                    </button>
                  }
                </div>
              }
            </div>
          }
        </div>
      </div>

      <full-calendar
        #calendar
        [options]="calendarOptions"
        [events]="filteredEvents$ | async"
      >
      </full-calendar>
      <div *ngIf="loadingEvents" class="loading-overlay">
        <p-progressSpinner ariaLabel="loading" class="m-auto" />
      </div>
    </div>
  </div>
</div>

<!-- Menu contextuel pour les événements -->
<p-contextMenu #contextMenu [model]="contextMenuItems"></p-contextMenu>

<!-- Confirmation Dialog pour déprogrammer -->
<p-confirmDialog [style]="{ width: '450px' }"></p-confirmDialog>

<!-- Remplacer la modale de déprogrammation par le nouveau composant -->
<app-unprogramming-modal
  [(visible)]="showUnprogramDialog"
  [selectedEvent]="selectedContextEvent"
  [shiftOrigins]="shiftOrigins"
  [loadingShiftOrigins]="loadingShiftOrigins"
  [submitting]="unprogramSubmitting"
  (onSubmit)="unprogramEvent($event)"
></app-unprogramming-modal>

<app-shift-modal
  [(visible)]="showShiftDialog"
  [selectedEvent]="selectedContextEvent"
  [shiftOrigins]="shiftOrigins"
  [loadingShiftOrigins]="loadingShiftOrigins"
  [submitting]="shiftSubmitting"
  [availableTechnicians]="usersForm.value.users || []"
  [allTechnicians]="allTechnicians"
  (onSubmit)="handleShiftSubmit($event)"
></app-shift-modal>

<!-- Utiliser le nouveau composant pour les détails d'événement -->
<app-event-details-modal
  [(visible)]="showEventDialog"
  [eventDetails]="selectedEventDetails"
></app-event-details-modal>

<!-- Remplacer le dialogue de sélection de date par le nouveau composant -->
<app-route-date-selector
  [(visible)]="showDateSelector"
  [technician]="selectedTechnicianForRoute"
  [(selectedDate)]="selectedDateForRoute"
  [minDate]="minDateForRoute"
  [maxDate]="maxDateForRoute"
  [visibleStartDate]="visibleStartDate"
  [visibleEndDate]="visibleEndDate"
  (generateRoute)="generateAndOpenRoute()"
></app-route-date-selector>

<app-new-modal
  [startTimeInput]="newModalStartTime"
  [durationInput]="newModalDuration"
  [(isVisible)]="showNewModal"
  (evenementCree)="onEvenementCree()"
>
</app-new-modal>

<!-- Modale d'édition d'événement -->
<app-edit-modal
  [(isVisible)]="showEditModal"
  [selectedEvent]="selectedContextEvent"
  (evenementModifie)="onEvenementModifie()"
>
</app-edit-modal>

<!-- Dialogue personnalisé pour les options de suppression récurrente -->
<p-dialog
  [(visible)]="showRecurrenceDeleteDialog"
  [modal]="true"
  header="Suppression d'événement récurrent"
  [style]="{ width: '500px' }"
  (onHide)="closeRecurrenceDeleteDialog()"
>
  <div class="flex flex-col gap-4">
    <div class="flex items-center gap-2 text-orange-500">
      <i class="pi pi-exclamation-triangle text-2xl"></i>
      <div>
        <p class="font-medium">
          Cet événement fait partie d'une série récurrente.
        </p>
        <p class="text-sm text-gray-600 dark:text-gray-400">
          Que souhaitez-vous supprimer ?
        </p>
      </div>
    </div>

    <div class="flex flex-col gap-3">
      <button
        type="button"
        pButton
        label="Cette occurrence uniquement"
        icon="pi pi-calendar"
        class="p-button-outlined p-button-secondary w-full justify-start"
        (click)="selectDeleteScope('this')"
      ></button>

      <button
        type="button"
        pButton
        label="Celle-ci et les futures"
        icon="pi pi-calendar-plus"
        class="p-button-outlined p-button-warning w-full justify-start"
        (click)="selectDeleteScope('thisAndFuture')"
      ></button>

      <button
        type="button"
        pButton
        label="Toutes les occurrences"
        icon="pi pi-calendar-times"
        class="p-button-outlined p-button-danger w-full justify-start"
        (click)="selectDeleteScope('all')"
      ></button>
    </div>

    <div class="flex justify-end pt-3 border-t">
      <button
        type="button"
        pButton
        label="Annuler"
        icon="pi pi-times"
        class="p-button-text"
        (click)="closeRecurrenceDeleteDialog()"
      ></button>
    </div>
  </div>
</p-dialog>
